import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
import { Icon } from 'js/components';
import ConfirmModal from 'js/components/modal/ConfirmModal';
import { useNotify } from 'js/hooks/useNotify';
import { faBoxArchive } from '@fortawesome/pro-solid-svg-icons';
import {
  formatSnapshotAsOf,
  optionalSnapshotName,
  snapshotDisplayName,
} from './org-snapshot-utils';

export default function OrgSnapshotSettings({
  snapshot,
  orgId,
  updateOrgSnapshot,
  onUpdated,
}) {
  const notify = useNotify();
  const [name, setName] = useState(snapshot.name ?? '');
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  useEffect(() => {
    setName(snapshot.name ?? '');
  }, [snapshot.id, snapshot.name]);

  const currentName = optionalSnapshotName(snapshot.name);
  const nextName = optionalSnapshotName(name);
  const nameDirty = nextName !== currentName;
  const isArchived = snapshot.archived_state === 'archived';
  const displayName = snapshotDisplayName(snapshot);

  const handleSaveName = useCallback(
    async (event) => {
      event.preventDefault();
      if (!nameDirty || saving) {
        return;
      }
      setSaving(true);
      try {
        await updateOrgSnapshot(orgId, snapshot.id, { name: nextName });
        await onUpdated?.();
        notify({ type: 'success', message: 'Snapshot name saved.' });
      } catch (err) {
        notify({
          type: 'danger',
          message: err?.message || 'Unable to save the snapshot name.',
        });
      } finally {
        setSaving(false);
      }
    },
    [
      nameDirty,
      nextName,
      notify,
      onUpdated,
      orgId,
      saving,
      snapshot.id,
      updateOrgSnapshot,
    ]
  );

  const handleArchive = useCallback(async () => {
    setArchiving(true);
    try {
      await updateOrgSnapshot(orgId, snapshot.id, {
        archived_state: 'archived',
      });
      await onUpdated?.();
      setShowArchiveModal(false);
      notify({ type: 'success', message: 'Snapshot archived.' });
    } catch (err) {
      notify({
        type: 'danger',
        message: err?.message || 'Unable to archive this snapshot.',
      });
    } finally {
      setArchiving(false);
    }
  }, [notify, onUpdated, orgId, snapshot.id, updateOrgSnapshot]);

  const handleRestore = useCallback(async () => {
    setArchiving(true);
    try {
      await updateOrgSnapshot(orgId, snapshot.id, { archived_state: null });
      await onUpdated?.();
      notify({ type: 'success', message: 'Snapshot restored.' });
    } catch (err) {
      notify({
        type: 'danger',
        message: err?.message || 'Unable to restore this snapshot.',
      });
    } finally {
      setArchiving(false);
    }
  }, [notify, onUpdated, orgId, snapshot.id, updateOrgSnapshot]);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="action-items">
            <Button
              color="secondary"
              onClick={
                isArchived ? handleRestore : () => setShowArchiveModal(true)
              }
              disabled={archiving}
            >
              <Icon icon={faBoxArchive} className="btn-icon-left" />
              {isArchived ? (archiving ? 'Restoring…' : 'Restore') : 'Archive'}
            </Button>
          </div>
          <CardTitle tag="h5">General</CardTitle>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSaveName}>
            <FormGroup>
              <Label for="snapshot-settings-name">
                Name <span className="text-muted">(optional)</span>
              </Label>
              <Input
                id="snapshot-settings-name"
                type="text"
                value={name}
                placeholder={formatSnapshotAsOf(snapshot.snapshot_at)}
                onChange={(event) => setName(event.target.value)}
                disabled={saving}
                className="limited-width-lg"
              />
            </FormGroup>
            <Button
              color="primary"
              type="submit"
              disabled={!nameDirty || saving}
            >
              {saving ? 'Updating…' : 'Update'}
            </Button>
          </Form>
        </CardBody>
      </Card>
      <ConfirmModal
        isOpen={showArchiveModal}
        title="Archive snapshot?"
        onConfirm={handleArchive}
        onCancel={() => setShowArchiveModal(false)}
        confirmButtonLabel="Archive"
      >
        <p>
          Please confirm that you want to archive <strong>{displayName}</strong>
          .
        </p>
        <p>Archived snapshots can be restored later.</p>
      </ConfirmModal>
    </>
  );
}
